import Avatar from '@mui/material/Avatar'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import { grey } from '@mui/material/colors'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React, { useMemo } from 'react'

import { extractNameInitials } from 'utils/string'
import { isValidHex } from 'utils/validations'

export type WeekDayWorkOrder = {
  routeId?: string | null
  routeName?: string | null
  routeColor?: string | null
}

type Props = {
  day: string
  workOrders?: WeekDayWorkOrder[]
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>
  onClick?: React.MouseEventHandler<HTMLDivElement>
}

const WeekDay: React.FC<Props> = ({ day, workOrders = [], onMouseEnter, onMouseLeave, onClick }) => {
  const theme = useTheme()
  const count = workOrders.length
  const hasWorkOrders = count > 0

  const { value, color, bgcolor } = useMemo(() => {
    const [first] = workOrders
    const isSingleWorkOrder = count === 1
    const isSingleWorkOrderAndUnassigned = isSingleWorkOrder && !first?.routeId
    if (isSingleWorkOrderAndUnassigned) {
      return { value: 'U', color: 'background.paper', bgcolor: grey[500] }
    }
    const isSingleWorkOrderAndHasRouteId = isSingleWorkOrder && first?.routeId
    if (isSingleWorkOrderAndHasRouteId) {
      const initials = extractNameInitials(first?.routeName)
      const routeColor = first?.routeColor
      const formattedBgcolor = routeColor && isValidHex(routeColor) ? routeColor : theme.palette.background.black
      return { value: initials, color: theme.palette.getContrastText(formattedBgcolor), bgcolor: formattedBgcolor }
    }

    return { value: count, color: 'background.paper', bgcolor: 'primary.main' }
  }, [count, workOrders, theme])

  return (
    <Box onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onClick={onClick}>
      <Badge
        badgeContent={value}
        invisible={!hasWorkOrders}
        sx={{
          '& .MuiBadge-badge': {
            color,
            backgroundColor: bgcolor,
            width: 24,
            height: 24,
            borderRadius: '50%',
            fontSize: 12,
          },
        }}
      >
        <Avatar
          sx={{
            width: 24,
            height: 24,
            bgcolor: grey[100],
            border: '1px solid',
            borderColor: hasWorkOrders ? 'action.active' : 'divider',
          }}
        >
          <Typography variant="subtitle2" color={hasWorkOrders ? 'textPrimary' : 'textSecondary'}>
            {day.charAt(0).toUpperCase()}
          </Typography>
        </Avatar>
      </Badge>
    </Box>
  )
}

export default WeekDay
